import { useCallback } from "react";
import type { Habit } from "@/context/SupabaseHabitContext";
import { createLocalDate } from "@/utils/dateUtils";
import { supabase, SUPABASE_ANON_KEY } from "@/integrations/supabase/client";

function cssColorToHex(cssColor: string): string {
  if (!cssColor || typeof cssColor !== "string") return "#252d3d";
  const trimmed = cssColor.trim();
  if (/^#[0-9A-Fa-f]{3,8}$/.test(trimmed)) return trimmed.length <= 5 ? trimmed : trimmed.slice(0, 7);
  if (typeof document === "undefined") return "#252d3d";
  const el = document.createElement("div");
  el.style.cssText = `position:absolute;left:-9999px;background:${trimmed}`;
  document.body.appendChild(el);
  const rgb = getComputedStyle(el).backgroundColor;
  document.body.removeChild(el);
  if (!rgb || rgb === "rgba(0, 0, 0, 0)") return "#252d3d";
  const m = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (!m) return "#252d3d";
  const hex = [Number(m[1]), Number(m[2]), Number(m[3])]
    .map((n) => n.toString(16).padStart(2, "0"))
    .join("");
  return "#" + hex;
}

function generateWidgetCode(
  habit: Habit,
  totalContributions: number,
  options?: { baseUrl: string; token: string | null; habitId: string; anonKey?: string }
): string {
  const completedDates = habit.completedDates ?? [];
  const today = new Date();
  const refDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const D = refDate.getDay();
  const gridData = new Array<number>(42).fill(0);
  for (const dateStr of completedDates) {
    const compDate = createLocalDate(dateStr);
    const daysAgo = Math.round((refDate.getTime() - compDate.getTime()) / (24 * 60 * 60 * 1000));
    if (daysAgo < 0 || daysAgo > 41) continue;
    const r = compDate.getDay();
    const weekNum = (daysAgo - D + r) / 7;
    if (weekNum % 1 !== 0 || weekNum < 0 || weekNum > 5) continue;
    const c = 5 - weekNum;
    gridData[r * 6 + c] = 1;
  }
  const gridDataJson = JSON.stringify(gridData);
  const filledHex = cssColorToHex(habit.color?.filled ?? "hsl(266, 70%, 65%)");
  const emptyHex = cssColorToHex(habit.color?.empty ?? "hsl(217, 33%, 17%)");
  const safeFilled = filledHex.replace(/"/g, '\\"');
  const safeEmpty = emptyHex.replace(/"/g, '\\"');

  if (options?.token && options?.baseUrl && options?.habitId) {
    const safeBase = options.baseUrl.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const safeToken = options.token.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const safeHabitId = options.habitId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const hasAnonKey = typeof options.anonKey === "string" && options.anonKey.length > 0;
    const safeAnonKey = hasAnonKey ? options.anonKey.replace(/\\/g, '\\\\').replace(/"/g, '\\"') : "";
    const anonKeyConst = hasAnonKey ? `const ANON_KEY = "${safeAnonKey}";` : "";
    const authHeaderLine = hasAnonKey ? "    req.headers = { \"Authorization\": \"Bearer \" + ANON_KEY };" : "";
    return `// Habit grid widget - Scriptable iOS (https://docs.scriptable.app/request/, ListWidget)
const BASE_URL = "${safeBase}";
const HABIT_ID = "${safeHabitId}";
const TOKEN = "${safeToken}";
const FALLBACK_GRID = ${gridDataJson};
${anonKeyConst}

async function fetchGridData() {
  try {
    const isSupabase = BASE_URL.indexOf("functions/v1") !== -1;
    const d = new Date();
    const today = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    const base = isSupabase
      ? BASE_URL + "/widget-data?habitId=" + encodeURIComponent(HABIT_ID) + "&token=" + encodeURIComponent(TOKEN)
      : BASE_URL + "/api/widgets/" + HABIT_ID + "?habitId=" + encodeURIComponent(HABIT_ID) + "&token=" + encodeURIComponent(TOKEN);
    const url = base + (isSupabase ? "&today=" + encodeURIComponent(today) : "");
    const req = new Request(url);
    req.method = "GET";
    req.timeoutInterval = 60;
${authHeaderLine}
    const raw = await req.loadString();
    const res = JSON.parse(raw);
    if (res && !res.error && Array.isArray(res.gridData)) {
      const arr = res.gridData;
      const grid = arr.length >= 42 ? arr.slice(0, 42) : arr.concat(Array(42 - arr.length).fill(0));
      return { gridData: grid, filledHex: res.filledHex || "#8b5cf6", emptyHex: res.emptyHex || "#252d3d" };
    }
  } catch (e) {}
  return null;
}

function drawGridImage(gridData, filledHex, emptyHex) {
  const rows = 7;
  const cols = 6;
  const cellSize = 10;
  const gap = 2;
  const corner = 3;
  const labelWidth = 12;
  const gridW = cols * cellSize + (cols - 1) * gap;
  const gridH = rows * cellSize + (rows - 1) * gap;
  const w = labelWidth + gridW;
  const h = gridH;
  const ctx = new DrawContext();
  ctx.size = new Size(w, h);
  ctx.respectScreenScale = true;
  const bgColor = new Color("#020617");
  const emptyColor = new Color(emptyHex);
  const filledColor = new Color(filledHex);
  const labelColor = new Color("#64748b");
  const now = new Date();
  const todayRow = now.getDay();
  const todayCol = 5;
  const todayStroke = new Color("#c4b5fd");
  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];
  ctx.setFillColor(bgColor);
  ctx.fillRect(new Rect(0, 0, w, h));
  ctx.setFont(Font.regularSystemFont(9));
  ctx.setTextColor(labelColor);
  for (let r = 0; r < rows; r++) {
    const rowY = r * (cellSize + gap);
    ctx.drawText(dayLabels[r], new Point(1, rowY + 3));
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      const filled = (gridData[idx] === 1 || gridData[idx] === "1" || Number(gridData[idx]) === 1);
      ctx.setFillColor(filled ? filledColor : emptyColor);
      const x = labelWidth + c * (cellSize + gap);
      const y = r * (cellSize + gap);
      const path = new Path();
      path.addRoundedRect(new Rect(x, y, cellSize, cellSize), corner, corner);
      ctx.addPath(path);
      ctx.fillPath();
    }
  }
  var todayX = labelWidth + todayCol * (cellSize + gap);
  var todayY = todayRow * (cellSize + gap);
  var todayPath = new Path();
  todayPath.addRoundedRect(new Rect(todayX, todayY, cellSize, cellSize), corner, corner);
  ctx.addPath(todayPath);
  ctx.setStrokeColor(todayStroke);
  ctx.setLineWidth(2);
  ctx.strokePath();
  return ctx.getImage();
}

async function run() {
  let gridData = FALLBACK_GRID;
  let filledHex = "${safeFilled}";
  let emptyHex = "${safeEmpty}";
  const api = await fetchGridData();
  if (api) {
    gridData = api.gridData;
    filledHex = api.filledHex;
    emptyHex = api.emptyHex;
  }
  const gridImg = drawGridImage(gridData, filledHex, emptyHex);
  const widget = new ListWidget();
  widget.backgroundColor = new Color("#020617");
  widget.setPadding(8, 8, 8, 8);
  const gridRow = widget.addImage(gridImg);
  gridRow.imageSize = new Size(114, 114);
  gridRow.centerAlignImage();
  widget.url = "habitualy://";
  if (config.runsInWidget) {
    widget.refreshAfterDate = new Date(Date.now() + 15 * 60 * 1000);
    Script.setWidget(widget);
  } else {
    await widget.presentMedium();
  }
  Script.complete();
}
run();`;
  }

  return `// Habit grid widget - Scriptable iOS, dark theme (https://docs.scriptable.app/)
const gridData = ${gridDataJson};
const filledHex = "${safeFilled}";
const emptyHex = "${safeEmpty}";

function drawGridImage() {
  const rows = 7;
  const cols = 6;
  const cellSize = 10;
  const gap = 2;
  const corner = 3;
  const labelWidth = 12;
  const gridW = cols * cellSize + (cols - 1) * gap;
  const gridH = rows * cellSize + (rows - 1) * gap;
  const w = labelWidth + gridW;
  const h = gridH;
  const ctx = new DrawContext();
  ctx.size = new Size(w, h);
  ctx.respectScreenScale = true;
  const bgColor = new Color("#020617");
  const emptyColor = new Color(emptyHex);
  const filledColor = new Color(filledHex);
  const labelColor = new Color("#64748b");
  const now = new Date();
  const todayRow = now.getDay();
  const todayCol = 5;
  const todayStroke = new Color("#c4b5fd");
  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];
  ctx.setFillColor(bgColor);
  ctx.fillRect(new Rect(0, 0, w, h));
  ctx.setFont(Font.regularSystemFont(9));
  ctx.setTextColor(labelColor);
  for (let r = 0; r < rows; r++) {
    const rowY = r * (cellSize + gap);
    ctx.drawText(dayLabels[r], new Point(1, rowY + 3));
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      const filled = gridData[idx] === 1;
      ctx.setFillColor(filled ? filledColor : emptyColor);
      const x = labelWidth + c * (cellSize + gap);
      const y = r * (cellSize + gap);
      const path = new Path();
      path.addRoundedRect(new Rect(x, y, cellSize, cellSize), corner, corner);
      ctx.addPath(path);
      ctx.fillPath();
    }
  }
  var todayX = labelWidth + todayCol * (cellSize + gap);
  var todayY = todayRow * (cellSize + gap);
  var todayPath = new Path();
  todayPath.addRoundedRect(new Rect(todayX, todayY, cellSize, cellSize), corner, corner);
  ctx.addPath(todayPath);
  ctx.setStrokeColor(todayStroke);
  ctx.setLineWidth(2);
  ctx.strokePath();
  return ctx.getImage();
}

if (config.runsInWidget) {
  const widget = new ListWidget();
  widget.backgroundColor = new Color("#020617");
  widget.setPadding(8, 8, 8, 8);
  const gridImg = drawGridImage();
  const gridRow = widget.addImage(gridImg);
  gridRow.imageSize = new Size(114, 114);
  gridRow.centerAlignImage();
  widget.url = "habitualy://";
  Script.setWidget(widget);
} else {
  const widget = new ListWidget();
  widget.backgroundColor = new Color("#020617");
  widget.setPadding(8, 8, 8, 8);
  const gridImg = drawGridImage();
  const gridRow = widget.addImage(gridImg);
  gridRow.imageSize = new Size(114, 114);
  gridRow.centerAlignImage();
  await widget.presentMedium();
}

Script.complete();`;
}

export function useWidgetDownload(habit: Habit) {
  const totalContributions = habit.completedDates?.length ?? 0;

  const getWidgetCode = useCallback(async (): Promise<string | null> => {
    try {
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string)?.replace(/\/$/, "");
      const functionsBase =
        supabaseUrl ? `${supabaseUrl}/functions/v1` : ((import.meta.env.VITE_APP_URL as string) || (typeof window !== "undefined" ? window.location.origin : ""));
      const baseUrl = functionsBase;
      const tokenPath = supabaseUrl ? "/widget-token" : "/api/widgets/token";
      const { data: { session } } = await supabase.auth.getSession();
      let token: string | null = null;
      if (session?.access_token) {
        try {
          const res = await fetch(`${baseUrl}${tokenPath}?habitId=${encodeURIComponent(habit.id)}`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (res.ok) {
            const json = await res.json();
            token = json.token ?? null;
          }
        } catch {
          token = null;
        }
      }
      const anonKey =
        (import.meta.env.VITE_SUPABASE_ANON_KEY as string) ||
        (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string) ||
        SUPABASE_ANON_KEY ||
        "";
      return generateWidgetCode(habit, totalContributions, {
        baseUrl,
        token,
        habitId: habit.id,
        anonKey: anonKey || undefined,
      });
    } catch (error) {
      console.error("Error generating widget code:", error);
      return null;
    }
  }, [habit, totalContributions]);

  const downloadWidgetCode = useCallback(async () => {
    const widgetCode = await getWidgetCode();
    if (typeof window === "undefined" || !widgetCode) return;
    try {
      const blob = new Blob([widgetCode], { type: "application/javascript" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${habit.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_widget.js`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (typeof window !== "undefined") alert("Failed to generate widget code. Please try again.");
    }
  }, [habit.name, getWidgetCode]);

  return { downloadWidgetCode, getWidgetCode };
}
